import { LoyaltyItemOffer } from "../LoyaltyOfferClass/LoyaltyItemOffer";
import {
  LoyaltyTargetConfig,
  LoyaltyTargetEntity,
  LoyaltyTargetType,
} from "../loyaltyTypes";
import { SubItemType } from "../types";

/**
 * Selection maths for guest-built offers (BOGO / bundle).
 *
 * A BOGO carries two target configs — `validity.requirements` (buy side, sent
 * to calculate as `triggers`) and `config.itemConfig` (reward side, sent as
 * `selections`); a bundle carries only the reward side. Both use the same
 * maths, so they are modelled as two groups of one shape.
 *
 * Pure: the caller resolves entity ids to menu sub-items via `resolveSubItem`.
 */

export enum OfferSelectionGroupKind {
  /** Paid items the offer requires — `validity.requirements`. */
  Buy = "BUY",
  /** Free/discounted items the offer gives — `config.itemConfig`. */
  Reward = "REWARD",
}

export enum OfferSelectionControl {
  Checkbox = "checkbox",
  Counter = "counter",
}

export type SelectableEntity = {
  entityId: string;
  subItem: SubItemType;
  /** Quantity the offer forces; above 0 the entity cannot be removed. */
  minQuantity: number;
  maxQuantity: number;
  control: OfferSelectionControl;
  isLocked: boolean;
};

export type OfferSelectionGroup = {
  kind: OfferSelectionGroupKind;
  /** `minimumItemCount` — how many the guest must end up with. */
  requiredCount: number;
  /** `maximumItemCount`, defaulting to `requiredCount` (an exact-count choice). */
  maxCount: number;
  entities: SelectableEntity[];
};

/** entityId → chosen quantity. Absent or 0 means "not selected". */
type OfferSelectionMap = Record<string, number>;

type OfferSelectionState = {
  [OfferSelectionGroupKind.Buy]: OfferSelectionMap;
  [OfferSelectionGroupKind.Reward]: OfferSelectionMap;
};

function isItemTargetConfig(config: LoyaltyTargetConfig | undefined): boolean {
  return config?.targetType === LoyaltyTargetType.ITEMS;
}

/** A side the offer page can put in front of the guest. */
function isBuildableSide(config: LoyaltyTargetConfig | undefined): boolean {
  return isItemTargetConfig(config) && Boolean(config?.targetEntities?.length);
}

/**
 * Whether the offer page can build this offer. CATEGORIES targets are
 * unsupported and must be hidden rather than rendered half-working.
 *
 * Either side alone is enough. A bundle has only a reward side; a BOGO whose
 * reward lands on the cart — a percentage off, a fixed amount, bonus points —
 * has only a buy side, and that buy side is still a list of items the guest
 * picks from. Requiring an item-level reward hid every one of those.
 */
export function isSupportedGuestBuiltOffer(offer: LoyaltyItemOffer): boolean {
  if (!offer.isGuestBuiltOffer()) {
    return false;
  }

  const buy = offer.getBuyTargetConfig();
  const reward = offer.getRewardTargetConfig();

  // A side that is present must be usable; an absent one is simply not shown.
  if (buy && !isBuildableSide(buy)) {
    return false;
  }

  if (reward && !isBuildableSide(reward)) {
    return false;
  }

  return Boolean(buy) || Boolean(reward);
}

/** Narrows any redeemed offer to a guest-built one the offer page can build. */
export function isGuestBuiltOfferRedeemed(
  offer: unknown
): offer is LoyaltyItemOffer {
  return offer instanceof LoyaltyItemOffer && isSupportedGuestBuiltOffer(offer);
}

function buildEntity(
  entity: LoyaltyTargetEntity,
  subItem: SubItemType
): SelectableEntity {
  const minQuantity = Math.max(entity.minimumQuantity ?? 0, 0);
  const maxQuantity = Math.max(entity.maximumQuantity ?? 1, minQuantity, 1);

  return {
    entityId: entity.entityId,
    subItem,
    minQuantity,
    maxQuantity,
    control:
      maxQuantity > 1
        ? OfferSelectionControl.Counter
        : OfferSelectionControl.Checkbox,
    isLocked: minQuantity > 0,
  };
}

function buildGroup(
  kind: OfferSelectionGroupKind,
  config: LoyaltyTargetConfig,
  resolveSubItem: (entityId: string) => SubItemType | undefined,
  isAvailable: (entityId: string) => boolean
): OfferSelectionGroup {
  /*
   * An item the kitchen has run out of is dropped exactly like one the menu no
   * longer carries: leaving it on the page invites a pick that only fails at
   * calculate. Whether enough remain to build the offer at all is settled
   * before this, by `isOfferApplicable`.
   */
  const entities = (config.targetEntities ?? []).flatMap((entity) => {
    const subItem = resolveSubItem(entity.entityId);

    return subItem && isAvailable(entity.entityId)
      ? [buildEntity(entity, subItem)]
      : [];
  });

  const requiredCount = Math.max(config.minimumItemCount ?? 0, 0);

  return {
    kind,
    requiredCount,
    maxCount: Math.max(config.maximumItemCount ?? requiredCount, requiredCount),
    entities,
  };
}

/** Buy group first, then reward; entity order follows `targetEntities`. */
export function buildSelectionGroups(
  offer: LoyaltyItemOffer,
  resolveSubItem: (entityId: string) => SubItemType | undefined,
  isAvailable: (entityId: string) => boolean = () => true
): OfferSelectionGroup[] {
  const groups: OfferSelectionGroup[] = [];

  const buy = offer.getBuyTargetConfig();
  if (buy && isItemTargetConfig(buy)) {
    groups.push(
      buildGroup(OfferSelectionGroupKind.Buy, buy, resolveSubItem, isAvailable)
    );
  }

  const reward = offer.getRewardTargetConfig();
  if (reward && isItemTargetConfig(reward)) {
    groups.push(
      buildGroup(
        OfferSelectionGroupKind.Reward,
        reward,
        resolveSubItem,
        isAvailable
      )
    );
  }

  return groups;
}

export function selectedCount(selections: OfferSelectionMap): number {
  return Object.values(selections).reduce(
    (total, quantity) => total + Math.max(quantity, 0),
    0
  );
}

/** Seeds the quantities the offer forces (`minimumQuantity`). */
function initialSelections(group: OfferSelectionGroup): OfferSelectionMap {
  const selections: OfferSelectionMap = {};

  for (const entity of group.entities) {
    if (entity.minQuantity > 0) {
      selections[entity.entityId] = entity.minQuantity;
    }
  }

  return selections;
}

export function initialSelectionState(
  groups: OfferSelectionGroup[]
): OfferSelectionState {
  const state: OfferSelectionState = {
    [OfferSelectionGroupKind.Buy]: {},
    [OfferSelectionGroupKind.Reward]: {},
  };

  for (const group of groups) {
    state[group.kind] = initialSelections(group);
  }

  return state;
}

function findEntity(
  group: OfferSelectionGroup,
  entityId: string
): SelectableEntity | undefined {
  return group.entities.find((entity) => entity.entityId === entityId);
}

/**
 * The one selection a group allows, or undefined when the guest has a choice.
 *
 * Fixed either way the counts can close it off: the minimums already reach
 * the group's maximum, so every pick is forced; or the maximums only just
 * reach its required count, so every item has to be taken in full. An unset
 * maximum is one, as everywhere else on the offer page.
 */
function forcedSelection(
  group: OfferSelectionGroup
): OfferSelectionMap | undefined {
  if (!group.entities.length) {
    return undefined;
  }

  const sumOf = (quantity: (entity: SelectableEntity) => number) =>
    group.entities.reduce((sum, entity) => sum + quantity(entity), 0);
  const pick = (quantity: (entity: SelectableEntity) => number) =>
    Object.fromEntries(
      group.entities
        .filter((entity) => quantity(entity) > 0)
        .map((entity) => [entity.entityId, quantity(entity)])
    );

  if (sumOf((entity) => entity.minQuantity) === group.maxCount) {
    return pick((entity) => entity.minQuantity);
  }
  if (sumOf((entity) => entity.maxQuantity) === group.requiredCount) {
    return pick((entity) => entity.maxQuantity);
  }

  return undefined;
}

/** The whole offer's forced selection — undefined when any group leaves a choice. */
export function forcedSelectionState(
  groups: OfferSelectionGroup[]
): OfferSelectionState | undefined {
  if (!groups.length) {
    return undefined;
  }

  const state: OfferSelectionState = {
    [OfferSelectionGroupKind.Buy]: {},
    [OfferSelectionGroupKind.Reward]: {},
  };

  for (const group of groups) {
    const forced = forcedSelection(group);
    if (!forced) {
      return undefined;
    }
    state[group.kind] = forced;
  }

  return state;
}

export function remainingCapacity(
  group: OfferSelectionGroup,
  selections: OfferSelectionMap
): number {
  return Math.max(group.maxCount - selectedCount(selections), 0);
}

export function canIncrement(
  group: OfferSelectionGroup,
  selections: OfferSelectionMap,
  entityId: string
): boolean {
  const entity = findEntity(group, entityId);
  if (!entity) {
    return false;
  }

  const current = selections[entityId] ?? 0;

  return (
    current < entity.maxQuantity && remainingCapacity(group, selections) > 0
  );
}

export function canDecrement(
  group: OfferSelectionGroup,
  selections: OfferSelectionMap,
  entityId: string
): boolean {
  const entity = findEntity(group, entityId);
  if (!entity) {
    return false;
  }

  return (selections[entityId] ?? 0) > entity.minQuantity;
}

/** Returns the same map reference when the step is not allowed. */
export function stepSelection(
  group: OfferSelectionGroup,
  selections: OfferSelectionMap,
  entityId: string,
  delta: number
): OfferSelectionMap {
  if (
    delta > 0
      ? !canIncrement(group, selections, entityId)
      : !canDecrement(group, selections, entityId)
  ) {
    return selections;
  }

  const next = { ...selections };
  const quantity = (next[entityId] ?? 0) + Math.sign(delta);

  if (quantity <= 0) {
    delete next[entityId];
  } else {
    next[entityId] = quantity;
  }

  return next;
}

/**
 * A pick-one group behaves like a radio: the new pick replaces the old one,
 * unless the old one is locked in. Toggling into any other full group is
 * refused, not an error — which pick to drop is the guest's call.
 */
export function toggleSelection(
  group: OfferSelectionGroup,
  selections: OfferSelectionMap,
  entityId: string
): OfferSelectionMap {
  const entity = findEntity(group, entityId);
  if (!entity) {
    return selections;
  }

  if ((selections[entityId] ?? 0) === 0) {
    const added = stepSelection(group, selections, entityId, 1);
    if (added !== selections || group.maxCount !== 1) {
      return added;
    }

    const holdsLockedPick = Object.keys(selections).some(
      (id) => findEntity(group, id)?.isLocked
    );
    if (holdsLockedPick) {
      return selections;
    }

    const cleared: OfferSelectionMap = {};
    const swapped = stepSelection(group, cleared, entityId, 1);

    return swapped === cleared ? selections : swapped;
  }

  if (entity.isLocked) {
    return selections;
  }

  const next = { ...selections };
  delete next[entityId];

  return next;
}

/** False when a tap would do nothing — a full multi-pick group. */
export function canSelectEntity(
  group: OfferSelectionGroup,
  selections: OfferSelectionMap,
  entityId: string
): boolean {
  return (
    (selections[entityId] ?? 0) > 0 ||
    toggleSelection(group, selections, entityId) !== selections
  );
}

export function isGroupComplete(
  group: OfferSelectionGroup,
  selections: OfferSelectionMap
): boolean {
  return selectedCount(selections) >= group.requiredCount;
}

/** The CTA's enable condition. */
export function isSelectionComplete(
  groups: OfferSelectionGroup[],
  state: OfferSelectionState
): boolean {
  return groups.every((group) => isGroupComplete(group, state[group.kind]));
}

export type SelectedEntry = {
  group: OfferSelectionGroupKind;
  entity: SelectableEntity;
  quantity: number;
};

/** Buy items before reward items, each group in `targetEntities` order. */
export function flattenSelection(
  groups: OfferSelectionGroup[],
  state: OfferSelectionState
): SelectedEntry[] {
  return groups.flatMap((group) =>
    group.entities.flatMap((entity) => {
      const quantity = state[group.kind][entity.entityId] ?? 0;

      return quantity > 0 ? [{ group: group.kind, entity, quantity }] : [];
    })
  );
}

export function selectedNames(
  group: OfferSelectionGroup,
  selections: OfferSelectionMap,
  getName: (entity: SelectableEntity) => string
): string[] {
  return group.entities.flatMap((entity) =>
    (selections[entity.entityId] ?? 0) > 0 ? [getName(entity)] : []
  );
}
